import React, { createContext, FC, PropsWithChildren, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TerminalAndLocation } from 'Src/feature/provision/provisionService';

enum Step {
  EnterToken,
  Review,
}

const useTokenWorkFlowStore = () => {
  const { t } = useTranslation();
  const [data, dataSet] = useState({
    token: null as string | null,
    terminal: undefined as TerminalAndLocation['terminal'] | undefined,
    location: undefined as TerminalAndLocation['location'] | undefined,
  });

  const orderedSteps = [Step.EnterToken, Step.Review];
  const [currentStep, currentStepSet] = useState(orderedSteps[0]);
  const [canGoNext, canGoNextSet] = useState(false);

  const dataRef = useRef(data);
  dataRef.current = data;

  const updateData = (updateData: Partial<typeof data>) => {
    dataRef.current = {
      ...dataRef.current,
      ...updateData,
    };
    dataSet(dataRef.current);
  };

  const goPrev = () => {
    const evtName: keyof HTMLElementEventMap = 'tokenWorkflow.onGoPrev';
    const evt = new CustomEvent(evtName, { cancelable: true });
    document.dispatchEvent(evt);
    if (evt.defaultPrevented) {
      return;
    }
    const idx = orderedSteps.indexOf(currentStep);
    const prev = orderedSteps[idx - 1];
    if (prev >= Step.EnterToken) {
      currentStepSet(prev);
    }
  };

  const goNext = () => {
    const evtName: keyof HTMLElementEventMap = 'tokenWorkflow.onGoNext';
    const evt = new CustomEvent(evtName, { cancelable: true });
    document.dispatchEvent(evt);
    if (evt.defaultPrevented) {
      return;
    }
    const idx = orderedSteps.indexOf(currentStep);
    const next = orderedSteps[idx + 1];
    if (next !== undefined) {
      currentStepSet(next);
    }
  };

  return {
    data,
    updateData,
    steps: {
      labels: {
        [Step.EnterToken]: t('provision.tokenWorkFlow.labels.enterToken'),
        [Step.Review]: t('provision.tokenWorkFlow.labels.claim'),
      },
      goPrev,
      goNext,
      get current() {
        return currentStep;
      },
      set current(next: Step) {
        currentStepSet(next);
      },
      get canGoPrev() {
        return currentStep !== Step.EnterToken;
      },
      get canGoNext() {
        return canGoNext;
      },
      set canGoNext(next) {
        canGoNextSet(next);
      },
      get isFinal() {
        return currentStep === Step.Review;
      },
      get isFirst() {
        return currentStep === Step.EnterToken;
      },
    },
  };
};

type UsedTokenWorkFlowStore = ReturnType<typeof useTokenWorkFlowStore>;

const TokenWorkFlowStoreContext = createContext<UsedTokenWorkFlowStore | undefined>(undefined);
TokenWorkFlowStoreContext.displayName = 'TokenWorkFlowStoreContext';

const TokenWorkFlowStoreProvider: FC<PropsWithChildren> = ({ children }) => {
  const store = useTokenWorkFlowStore();
  return (
    <TokenWorkFlowStoreContext.Provider value={store}>
      {children}
    </TokenWorkFlowStoreContext.Provider>
  );
};

const useTokenWorkflow = () => {
  const store = useContext(TokenWorkFlowStoreContext);
  if (!store) throw new Error('useTokenWorkflow() must be used within a TokenWorkFlowStoreProvider');
  return store;
};

export { Step, TokenWorkFlowStoreProvider, useTokenWorkflow };

